import { Moment } from 'moment'

import { ConfigItem, DefaultFile } from 'Core/interfaces'

import { ServiceLocation } from './ServiceLocation'

export enum SupplierInvoiceQueueStatusLabelTypes {
	initiated = 'initiated',
	loadReady = 'loader ready',
	confirmed = 'confirmed',
	confirmedException = 'confirmed-exception',
	inProgress = 'in progress',
	inProgressException = 'in progress-exception',
	approved = 'approved',
	voucherItemsCreated = 'voucher items created',
	cancelled = 'cancelled',
	transferred = 'transferred',
	default = 'default',
	issueNotResolved = 'issue not resolved',
	issueResolved = 'issue resolved',
	completed = 'completed',
	inLoaderQueue = 'in loader queue',
	indexed = 'indexed',
	manualReview = 'manual review',
	initiatedException = 'initiated-exception',
	movedToQueue = 'moved to queue'
}

export enum SupplierInvoiceQueueLabels {
	InvoiceScan = 'invoiceScan',
	BarCode = 'barCode',
	SupplierInvoiceNumber = 'supplierInvoiceNumber',
	IsAdminOnlySupplier = 'isAdminOnlySupplier',
	ServiceSiteId = 'serviceSiteId',
	SupplierLocationId = 'supplierLocationId',
	AccountingDate = 'accountingDate',
	InvoiceDate = 'invoiceDate',
	TotalInvoiceAmount = 'totalInvoiceAmount',
	ScanExceptionId = 'scanExceptionId',
	AdditionalComments = 'additionalComments',
	AdditionalNotes = 'additionalNotes',
	ScanExceptionType = 'scanExceptionType',
	ServiceSite = 'serviceSite',
	SupplierLocation = 'supplierLocation',
	LastUpdateDate = 'lastUpdateDate',
	ScanDate = 'scanDate',
	SupplierLocationComments = 'supplierLocationComments',
	ServiceSiteNotes = 'serviceSiteNotes',
	Status = 'status',
	AssignedToFirstName = 'assignedToFirstName',
	AssignedToLastName = 'assignedToLastName',
	ProgramManagerFirstName = 'programManagerFirstName',
	ProgramManagerLastName = 'programManagerLastName',
	LastChangeDate = 'lastChangeDate',
	Exception = 'exception',
	ScanException = 'scanException',
	Id = 'id',
	SupplierInvoiceId = 'supplierInvoiceId',
	InvoiceTypeId = 'invoiceTypeId',
	CurrencyId = 'currencyId',
	RemittanceAddressId = 'remittanceAddressId',
	BillingAgencyId = 'billingAgencyId',
	CheckNote = 'checkNote',
	InternalNote = 'internalNote',
	IsActive = 'isActive',
	AssignedTo = 'assignedTo',
	ProgramManager = 'programManager',
	RunningTotal = 'runningTotal',
	InvoiceType = 'invoiceType',
	Currency = 'currency',
	RemittanceAddress = 'remittanceAddress',
	BillingAgency = 'billingAgency',
	Comment = 'comment',
	ExceptionTypeId = 'exceptionTypeId',
	PSRemittAddress = 'pSRemittAddress',
	PSVendorId = 'pSVendorId',
	PSVendorLocation = 'pSVendorLocation',
	Supplier = 'supplier',
	PeopleSoftVendor = 'peopleSoftVendor',
	ServiceSitesAndNotes = 'serviceSitesAndNotes',
	ShortPayReasonId = 'shortPayReasonId',
	RequestReview = 'requestReview',
	AddressLine = 'addressLine',
	City = 'city',
	IsRemitAddressActive = 'isRemitAddressActive',
	IsVendorActive = 'isVendorActive',
	Location = 'location',
	Name = 'name',
	StateCode = 'stateCode',
	StateId = 'stateId',
	ZipCode = 'zipCode'
}

export enum SupplierInvoiceQueueSearchResultFields {
	searchResultByBarCodeNumber = 'searchResultByBarCodeNumber',
	searchResultBySupplierInvoiceNumber = 'searchResultBySupplierInvoiceNumber',
	searchResultBySupplierLocationId = 'searchResultBySupplierLocationId',
	searchResultBySupplierLocationName = 'searchResultBySupplierLocationName',
	searchResultByServiceSiteId = 'searchResultByServiceSiteId',
	searchResultByServiceSiteName = 'searchResultByServiceSiteName',
	searchResultByItemId = 'searchResultByItemId'
}

export enum SupplierInvoiceQueueFilterFields {
	assignedToId = 'assignedToId',
	roleCode = 'roleCode',
	invoiceDate = 'invoiceDate',
	statusId = 'statusId',
	exceptionTypeId = 'exceptionTypeId',
	exceptionId = 'exceptionId',
	scanDate = 'scanDate',
	onlyExceptions = 'onlyExceptions',
	rebatesOnly = 'rebatesOnly',
	overFiveDaysOnly = 'overFiveDaysOnly',
	myServiceTeamOnly = 'myServiceTeamOnly',
	includeInactive = 'includeInactive',
	scanTypeId = 'scanTypeId'
}

export enum SIQServiceDocumentFormFields {
	documentType = 'documentType',
	file = 'file'
}

export type DocumentEntity = {
	id: number
	documentNumber: string
}

export type ManifestServiceItem = {
	id: number
	pageNumber: number
	lineNumber: number | null
}

export type ServiceManifestMeta = {
	serviceOccurrenceId: BasicNumber
	scanRecordId: BasicNumber
	manifestId: BasicNumber
	serviceSite: Entity | null
}

export declare namespace SupplierInvoice {
	interface Basic {
		[SupplierInvoiceQueueLabels.Id]: number
		[SupplierInvoiceQueueLabels.BarCode]: string
		[SupplierInvoiceQueueLabels.SupplierInvoiceNumber]: string | null
		[SupplierInvoiceQueueLabels.SupplierLocation]:
			| (Entity & { isAdminOnlySupplier: boolean })
			| null
		[SupplierInvoiceQueueLabels.Status]: Entity | null
		[SupplierInvoiceQueueLabels.ServiceSite]: ServiceLocation.AddressEntity | null
		[SupplierInvoiceQueueLabels.AssignedToFirstName]: string | null
		[SupplierInvoiceQueueLabels.AssignedToLastName]: string | null
		[SupplierInvoiceQueueLabels.ProgramManagerFirstName]: string | null
		[SupplierInvoiceQueueLabels.ProgramManagerLastName]: string | null
		[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: number
		[SupplierInvoiceQueueLabels.InvoiceDate]: string
		[SupplierInvoiceQueueLabels.ScanDate]: string | null
		[SupplierInvoiceQueueLabels.LastChangeDate]: string | null
		[SupplierInvoiceQueueLabels.Exception]: string | null
		[SupplierInvoiceQueueLabels.SupplierInvoiceId]: number | null
		[SupplierInvoiceQueueLabels.IsActive]: boolean
	}

	interface PeopleSoftVendor {
		[SupplierInvoiceQueueLabels.Id]: BasicString
		[SupplierInvoiceQueueLabels.AddressLine]: BasicString
		[SupplierInvoiceQueueLabels.City]: BasicString
		[SupplierInvoiceQueueLabels.IsRemitAddressActive]: boolean
		[SupplierInvoiceQueueLabels.IsVendorActive]: boolean
		[SupplierInvoiceQueueLabels.Location]: BasicString
		[SupplierInvoiceQueueLabels.Name]: BasicString
		[SupplierInvoiceQueueLabels.StateCode]: BasicString
		[SupplierInvoiceQueueLabels.StateId]: number
		[SupplierInvoiceQueueLabels.ZipCode]: BasicString
	}

	interface Detailed
		extends Omit<
			Basic,
			| SupplierInvoiceQueueLabels.LastChangeDate
			| SupplierInvoiceQueueLabels.Exception
			| SupplierInvoiceQueueLabels.SupplierInvoiceId
		> {
		[SupplierInvoiceQueueLabels.InvoiceScan]: Entity | null
		[SupplierInvoiceQueueLabels.IsAdminOnlySupplier]: boolean
		[SupplierInvoiceQueueLabels.ScanException]: string | null
		[SupplierInvoiceQueueLabels.AdditionalComments]: string | null
		[SupplierInvoiceQueueLabels.SupplierLocationComments]: string | null
		[SupplierInvoiceQueueLabels.ServiceSiteNotes]: string | null
		[SupplierInvoiceQueueLabels.LastUpdateDate]: string | null
		[SupplierInvoiceQueueLabels.IsActive]: boolean
		[SupplierInvoiceQueueLabels.Currency]: Entity
		[SupplierInvoiceQueueLabels.Supplier]: Entity
		[SupplierInvoiceQueueLabels.PeopleSoftVendor]: PeopleSoftVendor
		// optional fields: not abaliable for invoice in status "initiated"
		[SupplierInvoiceQueueLabels.InvoiceType]?: Entity
		[SupplierInvoiceQueueLabels.RunningTotal]?: number
		[SupplierInvoiceQueueLabels.RemittanceAddress]?: RemittanceAddress
		[SupplierInvoiceQueueLabels.BillingAgency]?: Entity
		[SupplierInvoiceQueueLabels.CheckNote]?: string
		[SupplierInvoiceQueueLabels.InternalNote]?: string
	}

	interface BackupDocument {
		id: number
		documentType: Entity
		documentName: BasicString
		dateUploaded: BasicString
	}

	interface RemittanceAddress {
		id: number
		addressLine1: string
		stateCode: string
		city: string
	}

	interface ScanException extends Entity {
		routeName: string | null
	}

	interface AssociatedItem extends Entity {
		serviceSite: Entity
	}

	interface Contract {
		id: number
		contractNumber: BasicString
		contractFileName: BasicString
		startDate: BasicString
		endDate: BasicString
		evergreenAlertDate: BasicString
		sentDate: BasicString
		additionalInformation: BasicString
		internalNotes: BasicString
		associatedServiceSites: Entity[]
		associatedItems: AssociatedItem[]
	}

	interface Contracts {
		id: number
		contractNumber: string
		startDate: string
		endDate: string
		serviceSites: number[]
	}

	interface ValidationContact {
		firstName: string
		lastName: string
		lastUpdateDate: string
	}

	namespace Request {
		interface Create {
			[SupplierInvoiceQueueLabels.InvoiceScan]: File
			[SupplierInvoiceQueueLabels.BarCode]: string
			[SupplierInvoiceQueueLabels.SupplierInvoiceNumber]: string
			[SupplierInvoiceQueueLabels.ServiceSiteId]: number | null
			[SupplierInvoiceQueueLabels.SupplierLocationId]: number | null
			[SupplierInvoiceQueueLabels.InvoiceDate]: string | null
			[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: number
			[SupplierInvoiceQueueLabels.ScanExceptionId]: Key
			[SupplierInvoiceQueueLabels.AdditionalComments]: string | null
		}

		interface Edit {
			[SupplierInvoiceQueueLabels.BarCode]: string
			[SupplierInvoiceQueueLabels.SupplierInvoiceNumber]: string
			[SupplierInvoiceQueueLabels.InvoiceTypeId]: number | null
			[SupplierInvoiceQueueLabels.InvoiceDate]: string | null
			[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: number
			[SupplierInvoiceQueueLabels.CurrencyId]: Key
			[SupplierInvoiceQueueLabels.SupplierLocationId]: Key
			[SupplierInvoiceQueueLabels.RemittanceAddressId]: Key
			[SupplierInvoiceQueueLabels.ServiceSiteId]: Key | null
			[SupplierInvoiceQueueLabels.BillingAgencyId]: Key
			[SupplierInvoiceQueueLabels.CheckNote]: string | null
			[SupplierInvoiceQueueLabels.InternalNote]: string | null
		}
	}

	namespace Form {
		interface Create
			extends Omit<
				Request.Create,
				| SupplierInvoiceQueueLabels.InvoiceScan
				| SupplierInvoiceQueueLabels.InvoiceDate
				| SupplierInvoiceQueueLabels.ServiceSiteId
				| SupplierInvoiceQueueLabels.SupplierLocationId
				| SupplierInvoiceQueueLabels.TotalInvoiceAmount
			> {
			[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: string
			[SupplierInvoiceQueueLabels.InvoiceScan]: DefaultFile | null
			[SupplierInvoiceQueueLabels.InvoiceDate]: Moment | null
			[SupplierInvoiceQueueLabels.ScanExceptionType]: Key
			[SupplierInvoiceQueueLabels.ServiceSiteId]: LabelValue | null
			[SupplierInvoiceQueueLabels.SupplierLocationId]: LabelValue | null
			[SupplierInvoiceQueueLabels.IsAdminOnlySupplier]: boolean
		}

		interface Edit
			extends Omit<
				Request.Edit,
				| SupplierInvoiceQueueLabels.InvoiceDate
				| SupplierInvoiceQueueLabels.ServiceSiteId
				| SupplierInvoiceQueueLabels.SupplierLocationId
				| SupplierInvoiceQueueLabels.TotalInvoiceAmount
			> {
			[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: string
			[SupplierInvoiceQueueLabels.InvoiceDate]: Moment | null
			[SupplierInvoiceQueueLabels.SupplierLocationId]: LabelValue
			[SupplierInvoiceQueueLabels.ServiceSiteId]: NodeLabelValue | null
			[SupplierInvoiceQueueLabels.IsActive]: boolean
			[SupplierInvoiceQueueLabels.SupplierLocationComments]: string
			[SupplierInvoiceQueueLabels.ServiceSiteNotes]: string
			[SupplierInvoiceQueueLabels.AssignedTo]: string
			[SupplierInvoiceQueueLabels.ProgramManager]: string
			[SupplierInvoiceQueueLabels.RunningTotal]: number | string
		}
	}

	namespace Search {
		interface Config {
			[SupplierInvoiceQueueSearchResultFields.searchResultByBarCodeNumber]: ConfigItem<SupplierInvoiceQueueSearchResultFields>
			[SupplierInvoiceQueueSearchResultFields.searchResultBySupplierInvoiceNumber]: ConfigItem<SupplierInvoiceQueueSearchResultFields>
			[SupplierInvoiceQueueSearchResultFields.searchResultBySupplierLocationId]: ConfigItem<SupplierInvoiceQueueSearchResultFields>
			[SupplierInvoiceQueueSearchResultFields.searchResultBySupplierLocationName]: ConfigItem<SupplierInvoiceQueueSearchResultFields>
			[SupplierInvoiceQueueSearchResultFields.searchResultByServiceSiteId]: ConfigItem<
				SupplierInvoiceQueueSearchResultFields,
				Entity
			>
			[SupplierInvoiceQueueSearchResultFields.searchResultByServiceSiteName]: ConfigItem<
				SupplierInvoiceQueueSearchResultFields,
				Entity
			>
			[SupplierInvoiceQueueSearchResultFields.searchResultByItemId]: ConfigItem<SupplierInvoiceQueueSearchResultFields>
		}

		interface Result {
			[SupplierInvoiceQueueSearchResultFields.searchResultByBarCodeNumber]: Entity[]
			[SupplierInvoiceQueueSearchResultFields.searchResultBySupplierInvoiceNumber]: Entity[]
			[SupplierInvoiceQueueSearchResultFields.searchResultBySupplierLocationId]: Entity[]
			[SupplierInvoiceQueueSearchResultFields.searchResultByServiceSiteId]: ServiceLocation.AddressEntity[]
			[SupplierInvoiceQueueSearchResultFields.searchResultByServiceSiteName]: ServiceLocation.AddressEntity[]
			[SupplierInvoiceQueueSearchResultFields.searchResultBySupplierLocationName]: Entity[]
			[SupplierInvoiceQueueSearchResultFields.searchResultByItemId]: Entity[]
		}
	}

	interface ProgramManager {
		id: number
		firstName: string | null
		lastName: string | null
		middleInitial: string | null
	}

	namespace InvoiceScan {
		namespace Form {
			interface Add {
				invoiceScan: DefaultFile | null
			}
		}
		namespace Request {
			interface Add {
				invoiceScan: File | null
			}
		}
	}

	namespace InvoiceAction {
		interface FormData {
			additionalNotes: string
		}

		interface ReassignFormData extends FormData {
			userId: number
		}

		namespace Request {
			interface Edit extends FormData {
				scanRecordIds: number[]
			}

			interface Reassign extends Edit {
				userId: number
			}
		}
	}

	namespace History {
		namespace Exception {
			namespace Form {
				interface Add {
					[SupplierInvoiceQueueLabels.ExceptionTypeId]: Key | null
					[SupplierInvoiceQueueLabels.ScanExceptionId]: Key | null
					[SupplierInvoiceQueueLabels.AdditionalComments]: string
				}
			}

			interface Request {
				[SupplierInvoiceQueueLabels.ScanExceptionId]: Key | null
				[SupplierInvoiceQueueLabels.AdditionalComments]: string | null
				isInvoiceLoader?: boolean
			}
		}

		namespace Comment {
			namespace Form {
				interface Add {
					[SupplierInvoiceQueueLabels.Comment]: BasicString
					isInvoiceLoader?: boolean
				}
			}
		}
	}

	namespace ExtraCharge {
		interface Basic {
			id: number
			chargeCode: Entity | null
			unit: Entity | null
			quantity: number | null
			unitPrice: number | null
			extendedPrice: number | null
			serviceDate: string | null
			itemDescription: string | null
			serviceSite: Entity | null
			customBillingFrequency: Entity | null
			locality: Entity | null
		}

		interface BasicForTable {
			id: string
			serviceSite: Entity | null
			itemDescription: string | null
			quantity: number | null
			unitPrice: number | null
			unit: Entity | null
			extendedPrice: number | null
			serviceDate: string | null
			customBillFreqCode: string | null
			chargeCategory: string | null
		}

		namespace Form {
			interface Generic {
				serviceSite: LabelValue<number> | null
				customBillingFrequencyId: number | null
				chargeCategoryId: number | null
				unitId: number | null
				quantity: string | null
				unitPrice: string | null
				extendedPrice: string | null
				itemDescription: string | null
				serviceDate: Moment | null
				localityId: string | null
			}
		}

		namespace Request {
			interface Generic {
				chargeCodeId: number | null
				unitId: number | null
				quantity: number | null
				unitPrice: number | null
				extendedPrice: number | null
				serviceDate: string | null
				itemDescription: string | null
				serviceSiteId: number | null
				customBillingFrequencyId: number | null
				localityId: string | null
			}
		}

		namespace Search {
			namespace Result {
				interface CustomBillingFrequency {
					id: number
					customerName: string | null
					code: string
				}
			}
		}
	}

	namespace ExtraChargeAdminOnly {
		interface Basic {
			id: number
			serviceSite: Entity | null
			itemDescription: string | null
			quantity: number | null
			unitPrice: number | null
			unit: Entity | null
			extendedPrice: number | null
			serviceDate: string | null
			chargeCode: Entity | null
			glAccountCode: string | null
			departmentCode: string | null
			productCode: string | null
			affiliateCode: string | null
		}

		interface BasicForTable {
			id: number
			serviceSite: Entity | null
			itemDescription: string | null
			quantity: number | null
			unitPrice: number | null
			unit: Entity | null
			extendedPrice: number | null
			serviceDate: string | null
			chargeCategory: string | null
			customBillFreqCode: string | null
			glAccountCode: string | null
			departmentCode: string | null
			productCode: string | null
		}

		namespace Form {
			interface Generic {
				chargeCategoryId: number | null
				unitId: number | null
				quantity: string | null
				unitPrice: string | null
				extendedPrice: string | null
				itemDescription: string | null
				serviceDate: Moment | null
				glAccountCode: string | null
				departmentCode: string | null
				productCode: string | null
				affiliateCode: string | null
			}
		}

		namespace Request {
			interface Generic {
				chargeCodeId: number | null
				unitId: number | null
				quantity: number | null
				unitPrice: number | null
				extendedPrice: number | null
				serviceDate: string | null
				itemDescription: string | null
				glAccountCode: string | null
				departmentCode: string | null
				productCode: string | null
				affiliateCode: string | null
			}
		}
	}

	namespace Service {
		interface Basic {
			supplierInvoiceItemId: number
			customerOrderId: number
			serviceSite: (Entity & { comments: string }) | null
			item: {
				isPrebill: boolean
				isScale: boolean
				itemRefCode: string | null
				chargeTypeCode: Entity | null
				id: number
				name: string | null
				unit: string | null
			}
			supplierLocation: (Entity & { comments: string }) | null
			isManifestRequired: boolean
			hasTransactionDocument: boolean
			isDataTrackOnly: boolean
			items: {
				id: number
				itemName: string | null
				unit: string | null
				unitPrice: number
				quantity: number
				isItemParent: boolean
				extendedPrice: number
			}[]
			customerBillingInformation: {
				id: string
				billingItemId: number | null
				isPrimaryItem: boolean
				customerQuantity: number
				unit: string | null
				unitPrice: number | null
				expectedPrice: number | null
			}[]
			transactionId: number
			transactionItemId: number
			pounds: number
			kilograms: number
			containerQuantity: number
			initialDisposalFacility: Entity | null
			finalDisposalFacility: Entity | null
			supplierTransactionCode: string | null
			supplierTransactionCode2: string | null
			billOfLanding: string | null
			purchaseOrder: string | null
			serviceDate: string | null
			manifest: Entity | null
			manifestItem: ManifestServiceItem | null
			shortPayReason: Entity | null
			supplierCost: BasicNumber
			supplierAccountNumber: BasicString
			documentTypeId: BasicNumber
			documentFileName: BasicString
			documentId: BasicNumber
		}

		interface BasicForTable {
			supplierItemCost: number
			supplierInvoiceItemId: number
			serviceOccurrenceId: number
			serviceSiteId: number
			itemId: number
			itemName: string | null
			isItemParent: boolean
			quantity: number
			unitCost: number
			unit: string | null
			modifiedExtendedCost: number
			customerQuantity: number | null
			customerUnitCost: number | null
			customerUnit: string | null
			chargeCategory: string | null
			customBillFreqCode: string | null
			totalPrice: number | null
			serviceDate: string | null
			billOfLanding: string | null
			isManifestRequired: boolean
			hasTransactionDocument: boolean
			isDataTrackOnly: boolean
			weight: number | null
			documentFileName: BasicString
			documentId: BasicNumber
		}

		interface BasicWithDetailsForTable {
			id: Key
			supplierItemCost: number | null
			isExtraCharge: boolean
			supplierInvoiceItemId: number | null
			serviceOccurrenceId: number | null
			serviceSiteId: number | null
			itemId: number
			itemName: string | null
			isItemParent: boolean | null
			quantity: number | null
			unitCost: number | null
			unit: string | null
			modifiedExtendedCost: number | null
			customerQuantity: number | null
			customerUnitCost: number | null
			customerUnit: string | null
			chargeCategory: string | null
			customBillFreqCode: string | null
			totalPrice: number | null
			serviceDate: string | null
			billOfLanding: string | null
			isManifestRequired: boolean | null
			hasTransactionDocument: boolean | null
			isDataTrackOnly: boolean | string
			weight: number | null
			shortPayReason: Entity | null
			supplierCost: BasicNumber
			supplierAccountNumber?: BasicString
			documentFileName: BasicString
			documentId: BasicNumber
		}

		namespace Form {
			interface Add {
				serviceSiteId: NodeLabelValue<number> | null
				active: boolean
			}
			interface Edit {
				items: {
					itemId: number
					itemName: string | null
					unit: string | null
					unitPrice: string | null
					quantity: string | null
					isItemParent: boolean
					extendedPrice: string | null
				}[]
				customerQuantity: string | null
				pounds: string | null
				kilograms: string | null
				containerQuantity: string | null
				initialDisposalFacilityId: number | null
				finalDisposalFacilityId: number | null
				supplierTransactionCode: string | null
				supplierTransactionCode2: string | null
				billOfLanding: string | null
				purchaseOrder: string | null
				serviceDate: Moment | null
				transactionId: number | null
				transactionItemId: number | null
				manifest: LabelValue<number> | null
				manifestItemId: number | null
				shortPayReasonId: BasicNumber
				supplierCost: BasicString
				supplierAccountNumber: BasicString
				transactionDocumentType: BasicString
				transactionDocument: DefaultFile | { file: null; fileList: [] } | null
			}
		}

		namespace Request {
			interface Edit {
				items: {
					id: number
					unitPrice: number | null
					quantity: number | null
					extendedPrice: number | null
				}[]
				customerQuantity: number | null
				pounds: number | null
				kilograms: number | null
				containerQuantity: number | null
				initialDisposalFacilityId: number | null
				finalDisposalFacilityId: number | null
				supplierTransactionCode: string | null
				supplierTransactionCode2: string | null
				billOfLanding: string | null
				purchaseOrder: string | null
				serviceDate: string | null
				manifestId: number | null
				manifestItemId: number | null
				shortPayReasonId: BasicNumber
				supplierCost: BasicNumber
				supplierAccountNumber: BasicString
				file: File | null
				documentTypeId: BasicNumber
			}
		}

		namespace Search {
			namespace Result {
				namespace Edit {
					interface ManifestItem extends ManifestServiceItem {}

					interface Manifest {
						searchByDocumentNumber: DocumentEntity[]
						searchById: DocumentEntity[]
						searchByServiceOccurrenceId: Array<
							DocumentEntity & {
								serviceOccurrenceId: number
							}
						>
						searchByServiceSiteDocumentNumber: Array<
							DocumentEntity & {
								serviceSiteDocumentNumber: string
							}
						>
						searchByServiceSiteId: Array<
							DocumentEntity & {
								serviceSiteId: string
							}
						>
					}
				}
			}
		}

		namespace Item {
			interface Basic extends Form {
				itemId: number | null
				supplierDescription: string | null
				customerDescription: string | null
				isPrebill: boolean
				hasDependentItem: boolean
				hasScale: boolean
				hasFormula: boolean
				quantityUnits: string | null
				supplierCostUnits: string | null
				itemPriceUnits: string | null
				itemRef: string | null
				isActive: boolean
				supplierUnitOfMeasureId: number
				customerUnitOfMeasureId: number
			}

			interface Form {
				numberOfServices: number | null
				purchaseOrder: string | null
				quantity: number | null
				customerQuantity: BasicNumber
				serviceDate: Moment | null
			}
		}

		interface ServiceDetails {
			items: Item.Basic[]
			serviceSiteNotes: string | null
			isSubmitted: boolean
			isValid: boolean
			isLoading: boolean
			activeTab: string
			serviceSiteId: number | null
		}

		interface ExistingOccurrence {
			serviceDate: string
			customerOrderId: number
			serviceOccurrenceId: number
			itemId: number
			supplierDescription: string | null
			customerDescription: string | null
			cost: string | null
			quantity: number
		}

		interface ValidateRequest {
			serviceSiteId: number
			serviceOccurenceIds: number[]
			items: Array<
				Pick<
					Item.Basic,
					| 'itemId'
					| 'quantity'
					| 'customerQuantity'
					| 'numberOfServices'
					| 'purchaseOrder'
				> & {
					serviceDate: string | null
				}
			>
		}
		namespace Transaction {
			interface Basic {
				chargeTypeId: number | null
				cost: string | null
				itemDescription: string | null
				itemId: number | null
				orderId: number | null
				orderingUnitOfMeasure: string | null
				pounds: number | null
				quantity: number | null
				customerQuantity: number | null
				serviceDate: string | null
				serviceOccurrenceId: number | null
				hasScale: boolean
				hasFormula: boolean
				supplierUnitOfMeasureId: number
				customerUnitOfMeasureId: number
			}

			interface BasicDetails extends Omit<Basic, 'pounds' | 'quantity'> {
				billOfLanding: string | null
				supplierTransactionCode: string | null
				disposalFacilityId: number | null
				shortPayReasonId: BasicNumber
				supplierCost: BasicNumber
				supplierAccountNumber: BasicString
				pounds: string | null
				quantity: string | null
			}

			interface Form
				extends Pick<
					BasicDetails,
					| 'quantity'
					| 'customerQuantity'
					| 'pounds'
					| 'billOfLanding'
					| 'supplierTransactionCode'
					| 'disposalFacilityId'
					| 'supplierAccountNumber'
					| 'shortPayReasonId'
					| 'supplierCost'
				> {
				serviceDate: Moment | null
			}
			interface Request {
				transactions: {
					quantity: number | null
					customerQuantity: number | null
					billOfLanding: string | null
					serviceDate: string | null
					pounds: number | null
					supplierTransactionCode: string | null
					disposalFacilityId: number | null
					serviceOccurrenceId: number | null
					shortPayReasonId: number | null
					supplierCost: number | null
				}[]
			}
		}

		namespace Manifest {
			interface Basic {
				manifestId: number
				serviceOccurrenceId: number
				pdf: Entity | null
				transactionId: number | null
				serviceDate: string | null
				item: Entity | null
				manifestNumber: string | null
				serviceSiteName: string | null
				disposalFacilityName: string | null
				transporterNames: string[] | null
				page: number | null
				line: string | null
				wastestreamRefCode: string | null
				wastestreamName: string | null
				shippingName: string | null
				containerQuantity: number | null
				containerQuantityUnit: string | null
				weightLbs: number | null
				weightUnit: string | null
				weightVolume: number | null
				managementMethod: string | null
				form: string | null
				source: string | null
				epaHaz: string[] | null
				stateHaz: string[] | null
			}
		}

		namespace Document {
			namespace Form {
				interface Basic {
					[SIQServiceDocumentFormFields.documentType]: BasicString
					[SIQServiceDocumentFormFields.file]:
						| DefaultFile
						| { file: null; fileList: [] }
						| null
				}
				interface Valid {
					[SIQServiceDocumentFormFields.documentType]: string
					[SIQServiceDocumentFormFields.file]: DefaultFile
				}
			}

			namespace Request {
				interface Upload {
					file: File | null
					documentTypeId: number
					serviceOccurrenceIds: number[]
				}
			}
		}
	}

	namespace Finalize {
		namespace Form {
			interface Basic {
				[SupplierInvoiceQueueLabels.RunningTotal]: number | null
				[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: BasicString
				[SupplierInvoiceQueueLabels.AccountingDate]: Moment | null
				[SupplierInvoiceQueueLabels.CheckNote]: BasicString
				[SupplierInvoiceQueueLabels.InternalNote]: BasicString
				[SupplierInvoiceQueueLabels.ShortPayReasonId]: number | null
				[SupplierInvoiceQueueLabels.InvoiceTypeId]: number | null
				[SupplierInvoiceQueueLabels.RequestReview]: boolean
			}

			interface Valid extends Omit<Basic, SupplierInvoiceQueueLabels.TotalInvoiceAmount> {
				[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: string
				[SupplierInvoiceQueueLabels.InvoiceTypeId]: number
				[SupplierInvoiceQueueLabels.AccountingDate]: Moment
			}
		}

		interface Request
			extends Omit<
				Form.Valid,
				| SupplierInvoiceQueueLabels.RunningTotal
				| SupplierInvoiceQueueLabels.AccountingDate
				| SupplierInvoiceQueueLabels.TotalInvoiceAmount
			> {
			[SupplierInvoiceQueueLabels.AccountingDate]: string | null
			[SupplierInvoiceQueueLabels.TotalInvoiceAmount]: number
		}

		interface Duplicate {
			scanRecordId: number
			supplierInvoiceId: number
			supplierInvoiceNumber: string
			status: Entity
			reason: string
		}

		interface InvoiceAdjustment {
			description: BasicString
			solution: BasicString
		}
	}

	interface InvoiceHistory {
		id: number
		scanException: Entity | null
		scanExceptionType: Entity | null
		contactId: number
		contactFirstName: string
		contactLastName: string
		assignedUserId: number
		assignedUserFirstName: string
		assignedUserLastName: string
		routeQueueCode: string
		comment: string
		isResolved: boolean
		commentDate: string
	}
}
